// Updated FoodQuickLinksSection with permission-based navigation and centered icons
import React, { useState, useEffect } from 'react';
import { 
    Building, Settings, Utensils, ShoppingBag, 
    ChefHat, Truck, User, Tag, 
    Table, Package, BarChart, Home
} from 'lucide-react';
import { useNavigate } from "react-router-dom";

const FoodQuickLinksSection = ({ isMobile, onExport }) => {
    const [hoveredCard, setHoveredCard] = useState(null);
    const [allowedModules, setAllowedModules] = useState([]);
    const [allowedLinks, setAllowedLinks] = useState([]);
    const [screenWidth, setScreenWidth] = useState(0);
    const navigate = useNavigate();

    // Track screen width
    useEffect(() => {
        const updateScreenWidth = () => {
            setScreenWidth(window.innerWidth);
        };
        
        updateScreenWidth();
        window.addEventListener('resize', updateScreenWidth);
        return () => window.removeEventListener('resize', updateScreenWidth);
    }, []);

    const isTinyScreen = screenWidth <= 320;
    const isVerySmallScreen = screenWidth <= 375;
    const isSmallMobile = screenWidth <= 425;

    // All possible quick links with their module mapping
    const allQuickLinks = [
        {
            id: 1,
            title: "Business Profile",
            icon: <Building size={isTinyScreen ? 16 : isVerySmallScreen ? 18 : 20} />,
            color: "#8B5CF6",
            gradient: "linear-gradient(135deg, #8B5CF6 0%, #C084FC 100%)",
            description: "Restaurant details",
            path: "/foodbeveragescompany",
            moduleKey: "Company"
        },
        {
            id: 2,
            title: "Settings",
            icon: <Settings size={isTinyScreen ? 16 : isVerySmallScreen ? 18 : 20} />,
            color: "#64748B",
            gradient: "linear-gradient(135deg, #64748B 0%, #94A3B8 100%)",
            description: "System preferences",
            path: "/foodsettings",
            moduleKey: "Settings_Controls"
        },
        {
            id: 3,
            title: "Menu",
            icon: <Utensils size={isTinyScreen ? 16 : isVerySmallScreen ? 18 : 20} />,
            color: "#10B981",
            gradient: "linear-gradient(135deg, #10B981 0%, #34D399 100%)",
            description: "Food items & categories",
            path: "/foodandbeveragesMenu",
            moduleKey: "Menu_Management"
        },
        {
            id: 4,
            title: "Orders",
            icon: <ShoppingBag size={isTinyScreen ? 16 : isVerySmallScreen ? 18 : 20} />,
            color: "#F59E0B",
            gradient: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)",
            description: "Live & historical tracking",
            path: "/foodordermanagment",
            moduleKey: "Order_Management"
        },
        {
            id: 5,
            title: "Chef Panel",
            icon: <ChefHat size={isTinyScreen ? 16 : isVerySmallScreen ? 18 : 20} />,
            color: "#EF4444",
            gradient: "linear-gradient(135deg, #EF4444 0%, #F87171 100%)",
            description: "Kitchen management",
            path: "/foodchefmanagment",
            moduleKey: "Chef_Management"
        },
        {
            id: 6,
            title: "Delivery",
            icon: <Truck size={isTinyScreen ? 16 : isVerySmallScreen ? 18 : 20} />,
            color: "#3B82F6",
            gradient: "linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)",
            description: "Zones & partner setup",
            path: "/foodwaitermanagment",
            moduleKey: "Delivery_Management"
        },
        {
            id: 7,
            title: "Account",
            icon: <User size={isTinyScreen ? 16 : isVerySmallScreen ? 18 : 20} />,
            color: "#EC4899",
            gradient: "linear-gradient(135deg, #EC4899 0%, #F472B6 100%)",
            description: "Billing & transactions",
            path: "/foodAccuontdata",
            moduleKey: "Account_History"
        },
        {
            id: 8,
            title: "Promotions",
            icon: <Tag size={isTinyScreen ? 16 : isVerySmallScreen ? 18 : 20} />,
            color: "#F97316",
            gradient: "linear-gradient(135deg, #F97316 0%, #FB923C 100%)",
            description: "Offers & marketing",
            path: "/foodandbeveragespromotions",
            moduleKey: "Promotions_Discounts"
        },
        {
            id: 9,
            title: "Tables",
            icon: <Table size={isTinyScreen ? 16 : isVerySmallScreen ? 18 : 20} />,
            color: "#06B6D4",
            gradient: "linear-gradient(135deg, #06B6D4 0%, #22D3EE 100%)",
            description: "Reservations & seating",
            path: "/foodtablemanagment",
            moduleKey: "Table_Management"
        },
        {
            id: 10,
            title: "Inventory",
            icon: <Package size={isTinyScreen ? 16 : isVerySmallScreen ? 18 : 20} />,
            color: "#84CC16",
            gradient: "linear-gradient(135deg, #84CC16 0%, #A3E635 100%)",
            description: "Stock & supplies",
            path: "/foodandbeveragesInvetory",
            moduleKey: "Inventory_Management"
        },
        {
            id: 11,
            title: "Analytics",
            icon: <BarChart size={isTinyScreen ? 16 : isVerySmallScreen ? 18 : 20} />,
            color: "#6366F1",
            gradient: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
            description: "Reports & insights",
            path: "/foodandreporanalysis",
            moduleKey: "Report_Analysis"
        }
    ];

    // Permission-based filtering logic
    useEffect(() => {
        const employeeId = localStorage.getItem("employeeId");
        const loginData = JSON.parse(localStorage.getItem("loginResponse") || "{}");
        const modulesFromResponse = loginData?.modules || [];
        const planTypes = loginData?.planTypes || [];
        
        let filteredModules = [];

        if (employeeId && employeeId !== "0") {
            // Employee → show only modules from login response
            filteredModules = modulesFromResponse.map(m => m.trim().replace(/\s+/g, "_"));
        } else {
            // Parent vendor → show modules depending on plan type
            if (planTypes.includes("BASIC")) {
                filteredModules = [
                    "Company",
                    "Home",
                    "Settings_Controls",
                    "Order_Management",
                    "Menu_Management",
                    "Account_History",
                    "Report_Analysis",
                    "Promotions_Discounts"
                ];
            } else if (planTypes.includes("PREMIUM")) {
                // All modules are allowed for PREMIUM
                filteredModules = allQuickLinks.map(link => link.moduleKey);
            } else if (planTypes.includes("STANDARD")) {
                filteredModules = modulesFromResponse.length > 0
                    ? modulesFromResponse.map(m => m.trim().replace(/\s+/g, "_"))
                    : ["Home", "Order_Management"];
            } else {
                filteredModules = ["Home", "Order_Management"];
            }
        }

        // Ensure Dashboard is always included if it exists in the modules
        if (!filteredModules.includes("Dashboard") && modulesFromResponse.some(m => m.includes("Dashboard"))) {
            filteredModules.push("Dashboard");
        }

        setAllowedModules(filteredModules);

        // Filter the quick links based on allowed modules
        const filteredLinks = allQuickLinks.filter(link => 
            filteredModules.includes(link.moduleKey)
        );

        setAllowedLinks(filteredLinks);

    }, []);

    const handleLinkClick = (path) => {
        navigate(path);
    };

    // Function to check if a link should be visible
    const isLinkVisible = (moduleKey) => {
        return allowedModules.includes(moduleKey);
    };

    // Calculate grid columns based on screen size
    const getGridColumns = () => {
        if (isTinyScreen) return 'repeat(2, 1fr)';
        if (isVerySmallScreen) return 'repeat(2, 1fr)';
        if (isSmallMobile) return 'repeat(2, 1fr)';
        if (isMobile) return 'repeat(2, 1fr)';
        return 'repeat(4, 1fr)';
    };

    // Calculate gap based on screen size
    const getGap = () => {
        if (isTinyScreen) return '8px';
        if (isVerySmallScreen) return '12px';
        return '16px';
    };

    // Calculate card height based on screen size
    const getCardHeight = () => {
        if (isTinyScreen) return '130px';
        if (isVerySmallScreen) return '140px';
        if (isSmallMobile) return '150px';
        return '160px';
    };

    // Calculate padding based on screen size
    const getPadding = () => {
        if (isTinyScreen) return '12px 8px';
        if (isVerySmallScreen) return '14px 10px';
        if (isSmallMobile) return '16px 12px';
        return isMobile ? '16px 12px' : '0 24px';
    };

    return (
        <div style={{ 
            padding: getPadding(), 
            marginTop: '0px', 
            marginBottom: isTinyScreen ? '24px' : '32px',
            width: '100%',
            maxWidth: '100vw',
            boxSizing: 'border-box'
        }}>
            <div style={{ marginBottom: isTinyScreen ? '16px' : '20px' }}>
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: isTinyScreen ? '8px' : '12px', 
                    marginBottom: isTinyScreen ? '6px' : '8px' 
                }}>
                    <div style={{ 
                        width: '4px', 
                        height: isTinyScreen ? '18px' : '20px', 
                        background: 'linear-gradient(to bottom, #8B5CF6, #C084FC)', 
                        borderRadius: '2px' 
                    }}></div>
                    <h2 style={{ 
                        fontSize: isTinyScreen ? '18px' : isVerySmallScreen ? '20px' : '22px', 
                        fontWeight: '700', 
                        color: '#1E293B', 
                        margin: 0 
                    }}>
                        Quick Links
                    </h2>
                </div>
            </div>

            {allowedLinks.length === 0 ? (
                <div style={{ 
                    textAlign: 'center', 
                    padding: isTinyScreen ? '24px 16px' : '32px 24px', 
                    background: '#F8FAFC', 
                    borderRadius: isTinyScreen ? '12px' : '16px',
                    border: '2px dashed #CBD5E1',
                    margin: '0 4px'
                }}>
                    <p style={{ 
                        fontSize: isTinyScreen ? '14px' : '15px', 
                        color: '#64748B', 
                        margin: 0 
                    }}>
                        No quick links available based on your permissions.
                    </p>
                </div>
            ) : (
                <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: getGridColumns(), 
                    gap: getGap(),
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                }}>
                    {allowedLinks.map((link) => (
                        <div
                            key={link.id}
                            onMouseEnter={() => setHoveredCard(link.id)}
                            onMouseLeave={() => setHoveredCard(null)}
                            onClick={() => handleLinkClick(link.path)}
                            style={{ 
                                position: 'relative', 
                                cursor: 'pointer', 
                                height: getCardHeight(),
                                opacity: isLinkVisible(link.moduleKey) ? 1 : 0.5,
                                pointerEvents: isLinkVisible(link.moduleKey) ? 'auto' : 'none',
                                width: '100%'
                            }}
                            title={!isLinkVisible(link.moduleKey) ? "Not available in your plan" : ""}
                        >
                            <div style={{
                                background: '#FFFFFF',
                                borderRadius: isTinyScreen ? '12px' : '16px',
                                padding: isTinyScreen ? '12px' : isVerySmallScreen ? '16px' : '20px',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
                                border: `1.5px solid ${link.color}20`,
                                transition: 'all 0.2s ease',
                                transform: hoveredCard === link.id ? 'translateY(-2px)' : 'translateY(0)',
                                overflow: 'hidden',
                                position: 'relative',
                                width: '100%'
                            }}>
                                {/* Lock overlay for disabled items */}
                                {!isLinkVisible(link.moduleKey) && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: 'rgba(255, 255, 255, 0.9)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        zIndex: 2,
                                        borderRadius: isTinyScreen ? '12px' : '16px'
                                    }}>
                                        <div style={{
                                            background: '#CBD5E1',
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#64748B',
                                            fontSize: '14px'
                                        }}>
                                            🔒
                                        </div>
                                    </div>
                                )}

                                {/* Top gradient border */}
                                <div style={{ 
                                    position: 'absolute', 
                                    top: 0, 
                                    left: 0, 
                                    right: 0, 
                                    height: '3px', 
                                    background: link.gradient, 
                                    opacity: hoveredCard === link.id ? 1 : 0.8, 
                                    transition: 'opacity 0.2s ease' 
                                }}></div>

                                {/* Centered Icon Container */}
                                <div style={{ 
                                    width: isTinyScreen ? '40px' : isVerySmallScreen ? '44px' : '48px', 
                                    height: isTinyScreen ? '40px' : isVerySmallScreen ? '44px' : '48px', 
                                    background: link.gradient, 
                                    borderRadius: isTinyScreen ? '10px' : '12px', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    color: 'white', 
                                    marginBottom: isTinyScreen ? '8px' : '12px',
                                    marginTop: '4px',
                                    transform: hoveredCard === link.id ? 'scale(1.05)' : 'scale(1)', 
                                    transition: 'transform 0.2s ease',
                                    flexShrink: 0
                                }}>
                                    {link.icon}
                                </div>

                                {/* Text Content */}
                                <div style={{ 
                                    flex: 1, 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'center', 
                                    textAlign: 'center', 
                                    width: '100%',
                                    minWidth: 0
                                }}>
                                    <h3 style={{ 
                                        fontSize: isTinyScreen ? '13px' : isVerySmallScreen ? '14px' : '15px', 
                                        fontWeight: '600', 
                                        color: link.color, 
                                        margin: '0 0 6px 0', 
                                        lineHeight: '1.3',
                                        textAlign: 'center',
                                        width: '100%',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {link.title}
                                        {!isLinkVisible(link.moduleKey) && (
                                            <span style={{ 
                                                fontSize: '9px', 
                                                marginLeft: '4px', 
                                                color: '#94A3B8', 
                                                background: '#F1F5F9', 
                                                padding: '1px 4px', 
                                                borderRadius: '3px',
                                                display: 'inline-block'
                                            }}>
                                                Premium
                                            </span>
                                        )}
                                    </h3>
                                    <p style={{ 
                                        fontSize: isTinyScreen ? '11px' : isVerySmallScreen ? '12px' : '13px', 
                                        color: '#64748B', 
                                        margin: 0, 
                                        lineHeight: '1.4',
                                        textAlign: 'center',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        width: '100%'
                                    }}>
                                        {link.description}
                                    </p>
                                </div>

                                {/* Hover Background */}
                                <div style={{ 
                                    position: 'absolute', 
                                    top: 0, 
                                    left: 0, 
                                    right: 0, 
                                    bottom: 0, 
                                    background: link.gradient, 
                                    opacity: hoveredCard === link.id ? 0.05 : 0, 
                                    transition: 'opacity 0.2s ease', 
                                    pointerEvents: 'none' 
                                }}></div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add some CSS styles for better mobile rendering */}
            <style>{`
                /* Ensure proper touch targets on mobile */
                @media (max-width: 320px) {
                    .quick-link-card {
                        min-height: 130px !important;
                    }
                    
                    /* Improve touch feedback */
                    * {
                        -webkit-tap-highlight-color: transparent;
                    }
                    
                    /* Prevent text overflow */
                    .link-title {
                        max-width: 100% !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default FoodQuickLinksSection;